/**
 * ClassificationSelect - Select de classificação com hierarquia por categoria
 * 
 * Exibe classificações agrupadas por categoria, com ícone de cor configurada
 * e indentação para indicar hierarquia (parent → child).
 * Classificações pai com filhos ficam desabilitadas para seleção.
 * 
 * @version 1.1.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-06 v1.1.0 - Desabilitar seleção de pais com filhos
 */

import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import type { TaskClassification } from '@/hooks/useTaskClassifications';

interface ClassificationSelectProps {
  /** Valor selecionado (id) */
  value: string;
  /** Callback ao mudar valor */
  onChange: (value: string) => void;
  /** Lista de classificações (já filtradas por categoria ou não) */
  classifications: TaskClassification[];
  /** Placeholder */
  placeholder?: string;
  /** Se true, remove a opção "Nenhuma" (campo obrigatório) */
  required?: boolean;
}

/**
 * Organiza classificações em hierarquia agrupada por categoria
 */
function buildHierarchy(classifications: TaskClassification[]) {
  // Agrupa por categoria
  const byCategory = new Map<string, {
    categoryName: string;
    categoryColor: string;
    items: TaskClassification[];
  }>();

  for (const cls of classifications) {
    if (!byCategory.has(cls.category_id)) {
      byCategory.set(cls.category_id, {
        categoryName: cls.category_name,
        categoryColor: '',
        items: [],
      });
    }
    byCategory.get(cls.category_id)!.items.push(cls);
  }

  // Para cada categoria, ordena: pais primeiro, filhos depois
  const result: Array<{
    categoryId: string;
    categoryName: string;
    entries: Array<{ cls: TaskClassification; isChild: boolean; hasChildren: boolean }>;
  }> = [];

  for (const [catId, group] of byCategory) {
    const parents = group.items.filter(c => !c.parent_id).sort((a, b) => a.sort_order - b.sort_order);
    const childrenMap = new Map<string, TaskClassification[]>();

    for (const cls of group.items) {
      if (cls.parent_id) {
        if (!childrenMap.has(cls.parent_id)) childrenMap.set(cls.parent_id, []);
        childrenMap.get(cls.parent_id)!.push(cls);
      }
    }

    const entries: Array<{ cls: TaskClassification; isChild: boolean; hasChildren: boolean }> = [];
    for (const parent of parents) {
      // Pai com filhos fica desabilitado para seleção
      const hasChildren = (childrenMap.get(parent.id) || []).length > 0;
      entries.push({ cls: parent, isChild: false, hasChildren });
      const children = (childrenMap.get(parent.id) || []).sort((a, b) => a.sort_order - b.sort_order);
      for (const child of children) {
        entries.push({ cls: child, isChild: true, hasChildren: false });
      }
    }

    // Classificações órfãs (parent_id aponta para inexistente)
    const allParentIds = new Set(parents.map(p => p.id));
    const orphans = group.items.filter(c => c.parent_id && !allParentIds.has(c.parent_id));
    for (const orphan of orphans) {
      entries.push({ cls: orphan, isChild: true, hasChildren: false });
    }

    result.push({ categoryId: catId, categoryName: group.categoryName, entries });
  }

  return result;
}

/**
 * Select de classificação com hierarquia visual
 */
export function ClassificationSelect({
  value,
  onChange,
  classifications,
  placeholder = 'Nenhuma',
  required = false,
}: ClassificationSelectProps) {
  const hierarchy = useMemo(() => buildHierarchy(classifications), [classifications]);
  const hasMultipleCategories = hierarchy.length > 1;

  return (
    <Select value={value || (required ? '' : 'none')} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
      <SelectTrigger>
        <SelectValue placeholder={required ? 'Selecione...' : placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-[280px] w-[var(--radix-select-trigger-width)]">
        {/* Opção "Nenhuma" só aparece quando não é obrigatório */}
        {!required && <SelectItem value="none">Nenhuma</SelectItem>}
        {hierarchy.map((group) => (
          <SelectGroup key={group.categoryId}>
            {/* Exibe label de categoria quando há múltiplas */}
            {hasMultipleCategories && (
              <SelectLabel className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block opacity-60"
                  style={{ backgroundColor: group.entries[0]?.cls.color || '#888' }}
                />
                {group.categoryName} (Categoria)
              </SelectLabel>
            )}
            {group.entries.map(({ cls, isChild, hasChildren }) => (
              <SelectItem key={cls.id} value={cls.id} disabled={hasChildren}>
                <span className="flex items-center gap-2">
                  {isChild && <span className="text-muted-foreground ml-1">└─</span>}
                  <span
                    className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                    style={{ backgroundColor: cls.color }}
                  />
                  <span className="truncate max-w-[280px] inline-block">{cls.name}</span>
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
